import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import moment from 'moment';
import styled from 'styled-components';
import { useTheme } from '@material-ui/core/styles';
import { GridOptions } from 'ag-grid-community';
import { LicenseManager } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { groupBy, sortBy, values } from 'lodash-es';
import { Observation, Pim, Printing } from '@mummssoftware/utils';
import type { PrintProgress } from '@mummssoftware/utils/api/Printing';
import {
  AgGridTableWrapper,
  AgGridWidgetWrapper,
  ReactDatePicker,
  PrintProgressLoader,
} from '@mummssoftware/common-ui';
import 'ag-grid-community/dist/styles/ag-grid.css';
import 'ag-grid-community/dist/styles/ag-theme-alpine.css';
import '@mummssoftware/common-ui/style/css/ag-theme-hummingbird.css';
import defaults from '../../translations/en.json';
import { useCrossPatientIDGData } from './useCrossPatientIDGData';
import { PatientInMeeting, IDGMeeting, patientRowKey } from './transforms';
import { commonColumnDefs, getMasterColumnDefs } from './columnDefs';
import {
  IDGSelectionContext,
  IDGSelectionContextValue,
  SelectedPatientRow,
} from './IDGSelectionContext';
import { PatientDetailRenderer } from './PatientDetailRenderer';
import { CrossPatientIdgPrintFaxHandler } from './CrossPatientIdgPrintFaxHandler';

LicenseManager &&
  LicenseManager.setLicenseKey(process.env.REACT_APP_AG_GRID_KEY as string);

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export type CrossPatientIDGWidgetProps = {
  embedded?: boolean;
  agency?: string | null;
  siteId?: string | number | null;
  keycloak: Keycloak.KeycloakInstance;
  smallWidget?: boolean;
  appTitle?: string;
  currentUser?: mumms.User;
  /** Pre-constructed Pim instance (must have pgRest); created internally if omitted. */
  pim?: Pim;
  /** Pre-constructed Observation instance; one is created internally if omitted. */
  observation?: Observation;
  onClick?: () => void;
  onRemove?: () => void;
  doNotShow?: boolean;
  onWidgetReady?: (arg: mumms.ComponentName) => void;
};

// ---------------------------------------------------------------------------
// Layout
// ---------------------------------------------------------------------------

const AppRoot = styled.div<{ embedded?: boolean }>`
  margin-left: var(--safe-area-inset-left);
  margin-right: var(--safe-area-inset-right);
  margin-bottom: var(--safe-area-inset-bottom);
  overflow: hidden;
  position: relative;
  ${(props) => (props.embedded ? `height: initial;` : `height: 100vh;`)}
  display: flex;
  @media print {
    display: block;
    height: auto;
  }
`;

const AppContent = styled.div`
  width: 100%;
  @media print {
    height: auto;
  }
`;

// Keep in sync with the actual rendered height so AgGridWidgetWrapper's
// theme-based height calculation can subtract it.
const DATE_BAR_HEIGHT_PX = 42;

const DateRangeBar = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.4rem 0.75rem;
  background-color: ${(props) => props.theme.palette.background.paper};
  border-bottom: 1px solid ${(props) => props.theme.palette.divider};
  flex-wrap: wrap;
`;

const DateRangeLabel = styled.span`
  font-size: 0.8rem;
  color: ${(props) => props.theme.palette.text.secondary};
  white-space: nowrap;
`;

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const PIMREADER_DATE_FORMAT = 'YYYY-MM-DD';
const DEFAULT_LOOKBACK_DAYS = 365;
/** Fallback when theme.props.grid.headerHeight is unset (common theme omits it). */
const DEFAULT_GRID_HEADER_HEIGHT = 27;
const DEFAULT_GRID_ROW_HEIGHT = 27;

const toPimDate = (d: Date): string => moment(d).format(PIMREADER_DATE_FORMAT);

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Cross-patient IDG widget using AG Grid with master-detail.
 *
 * Master rows are IDG meetings (date range + patient count).
 * Expanding a master row reveals detail rows for patients reviewed at that
 * meeting. Detail rows are multi-selectable; the widget header print icon
 * prints all selected rows, grouped by patient with a coversheet per patient.
 */
export const CrossPatientIDGWidget = ({
  embedded,
  agency,
  siteId,
  keycloak,
  smallWidget,
  appTitle,
  currentUser,
  pim: pimProp,
  observation: observationProp,
  onClick,
  onRemove,
  doNotShow,
  onWidgetReady,
}: CrossPatientIDGWidgetProps) => {
  const {
    props: { grid: gridTheme },
  } = useTheme() as any;

  const resolvedAgency = agency ?? currentUser?.currentHospiceBin ?? undefined;
  const resolvedSiteId = siteId != null ? String(siteId) : undefined;

  // Pim is used for data fetching from pimreader.
  const pim = useMemo(
    () =>
      pimProp ??
      new Pim({
        url: window.env.hosts.pimServiceUrl as string,
        pgRestUrl: window.env.hosts.pimReader as string,
        keycloak,
        agency: resolvedAgency,
      }),
    [pimProp, keycloak, resolvedAgency],
  );

  // Observation is used only for fetching manifests at print time.
  const observation = useMemo(
    () =>
      observationProp ??
      new Observation({
        url: process.env.REACT_APP_OBSERVATION_URL as string,
        keycloak,
        agency: resolvedAgency,
      }),
    [observationProp, keycloak, resolvedAgency],
  );

  // Printing service instance.
  const printingService = useMemo(
    () =>
      new Printing({
        url: window.env.hosts.printingService as string,
        keycloak,
        agency: resolvedAgency ?? '',
        currentUser: currentUser as any,
      }),
    [keycloak, resolvedAgency, currentUser],
  );

  const [printProgress, setPrintProgress] = useState<PrintProgress | null>(
    null,
  );
  const [, setFetchingPdf] = useState(false);
  const [selectedRows, setSelectedRows] = useState<SelectedPatientRow[]>([]);

  // ---------------------------------------------------------------------------
  // Date range state
  // ---------------------------------------------------------------------------

  const [fromDate, setFromDate] = useState<Date>(() =>
    moment().subtract(DEFAULT_LOOKBACK_DAYS, 'days').toDate(),
  );
  const [toDate, setToDate] = useState<Date>(() => moment().toDate());

  const dateFrom = toPimDate(fromDate);
  const dateTo = toPimDate(toDate);

  // ---------------------------------------------------------------------------
  // Data
  // ---------------------------------------------------------------------------

  const { meetings, loading, error } = useCrossPatientIDGData(
    pim,
    resolvedSiteId,
    dateFrom,
    dateTo,
  );

  useEffect(() => {
    setSelectedRows([]);
  }, [dateFrom, dateTo]);

  // ---------------------------------------------------------------------------
  // Widget ready signal
  // ---------------------------------------------------------------------------

  // Guard so onWidgetReady is called at most once per mount, regardless of how
  // many times the parent re-renders and passes a new function reference.
  const widgetReadyCalled = useRef(false);
  useEffect(() => {
    if (!loading && !widgetReadyCalled.current && onWidgetReady) {
      widgetReadyCalled.current = true;
      onWidgetReady('IDGCrossPatient' as mumms.ComponentName);
    }
    // Intentionally omitting onWidgetReady: callers (DashboardGrid) create a
    // new function reference on every render; including it would cause an
    // infinite dispatch loop that starves the main thread and prevents clicks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  const gridHeaderHeight =
    gridTheme?.headerHeight ?? DEFAULT_GRID_HEADER_HEIGHT;
  const gridRowHeight = gridTheme?.rowHeight ?? DEFAULT_GRID_ROW_HEIGHT;

  const onGridReady = useCallback(
    (params: any) => {
      params.api.setHeaderHeight(gridHeaderHeight);
    },
    [gridHeaderHeight],
  );

  // While loading, omit rowData so AG Grid shows overlayNoRowsTemplate (not an
  // empty row set). Same pattern as DocumentList and cert-app ReviewListingGrid.
  const gridRowData = loading ? undefined : meetings;

  // ---------------------------------------------------------------------------
  // Detail row selection
  // ---------------------------------------------------------------------------

  const togglePatientRow = useCallback(
    (meetingKey: number, patient: PatientInMeeting) => {
      if (patient.patientManifestId == null) return;
      const key = patientRowKey(meetingKey, patient);
      setSelectedRows((prev) => {
        const exists = prev.some(
          (row) => patientRowKey(row.meetingKey, row.patient) === key,
        );
        if (exists) {
          return prev.filter(
            (row) => patientRowKey(row.meetingKey, row.patient) !== key,
          );
        }
        return [...prev, { meetingKey, patient }];
      });
    },
    [],
  );

  const isPatientRowSelected = useCallback(
    (meetingKey: number, patient: PatientInMeeting) =>
      selectedRows.some(
        (row) =>
          patientRowKey(row.meetingKey, row.patient) ===
          patientRowKey(meetingKey, patient),
      ),
    [selectedRows],
  );

  const selectionContextValue: IDGSelectionContextValue = useMemo(
    () => ({ togglePatientRow, isPatientRowSelected }),
    [togglePatientRow, isPatientRowSelected],
  );

  const hasSelection = selectedRows.length > 0;

  const clearMeetingSelections = useCallback((meetingKey: number) => {
    setSelectedRows((prev) =>
      prev.filter((row) => row.meetingKey !== meetingKey),
    );
  }, []);

  const onRowGroupOpened = useCallback(
    (event: { node: { expanded: boolean }; data: IDGMeeting }) => {
      if (!event.node.expanded) {
        clearMeetingSelections(event.data.meetingKey);
      }
    },
    [clearMeetingSelections],
  );

  // ---------------------------------------------------------------------------
  // Header print / fax
  // ---------------------------------------------------------------------------

  const getPrintPromise = useCallback(
    async (faxInstructions: any, emailInstructions: any) => {
      const printableRows = selectedRows.filter(
        (row) => row.patient.patientManifestId != null,
      );
      if (!printableRows.length) return Promise.reject();

      const patientGroups = sortBy(
        values(groupBy(printableRows, (row) => row.patient.patientNumber)),
        [
          (rows) => rows[0].patient.patientLastName,
          (rows) => rows[0].patient.patientFirstName,
        ],
      );

      const fetched = await Promise.all(
        patientGroups.map(async (rows) => {
          const { patientNumber } = rows[0].patient;
          const [fullPatient, ...forms] = await Promise.all([
            pim.getPatientHeader(patientNumber, resolvedAgency ?? ''),
            ...rows.map((row) =>
              observation.getFajitaFormByIdByMode(
                row.patient.patientManifestId as number,
                undefined,
                resolvedAgency,
              ),
            ),
          ]);
          return { fullPatient, forms };
        }),
      );

      const items = fetched
        .filter(
          ({ fullPatient, forms }) =>
            !!fullPatient &&
            !(fullPatient instanceof Response) &&
            forms.some((form) => form && !(form instanceof Response)),
        )
        .map(({ fullPatient, forms }) => ({
          route: 'print-manifest',
          payload: {
            patient: fullPatient as mumms.patient,
            agency: resolvedAgency,
            forms: forms
              .filter((form) => form && !(form instanceof Response))
              .map((form) => (form as unknown) as mumms.FormDTO),
            displayFacesheet: false,
          },
        }));

      if (!items.length) return Promise.reject();
      return printingService.printAssemblage(
        items,
        (progress) => setPrintProgress(progress),
        faxInstructions,
        emailInstructions,
      );
    },
    [selectedRows, pim, observation, printingService, resolvedAgency],
  );

  // ---------------------------------------------------------------------------
  // Grid options
  // ---------------------------------------------------------------------------

  const masterColumnDefs = useMemo(() => getMasterColumnDefs(smallWidget), [
    smallWidget,
  ]);

  // Keep gridOptions stable for master-detail; pass columnDefs and headerHeight as
  // AgGridReact props (same pattern as DocumentList). Do not set headerHeight: 0
  // here — that was only for the old nested detail grid and collapses master headers.
  const gridOptions: GridOptions = {
    masterDetail: !smallWidget,
    embedFullWidthRows: true,
    detailRowAutoHeight: true,
    isRowMaster: (data: any) => (data.patients?.length ?? 0) > 0,
    defaultColDef: { ...commonColumnDefs },
    detailCellRenderer: 'patientDetailRenderer',
    suppressCellSelection: true,
    suppressScrollOnNewData: true,
    onRowGroupOpened,
  };

  let overlayNoRowsTemplate = defaults.noMeetings;
  if (loading) {
    overlayNoRowsTemplate = defaults.loading;
  } else if (error) {
    overlayNoRowsTemplate = `<span>${error}</span>`;
  }

  const versionInfo = `${defaults.title} v${process.env.REACT_APP_IDG_DOCUMENT_VERSION}, SmartChart v${process.env.REACT_APP_VERSION}`;

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <IDGSelectionContext.Provider value={selectionContextValue}>
      <AppRoot embedded={embedded}>
        {printProgress !== null && (
          <PrintProgressLoader
            progress={printProgress}
            sourceString={defaults.title}
          />
        )}
        <AppContent>
          <CrossPatientIdgPrintFaxHandler
            appTitle={appTitle ?? defaults.title}
            versionInfo={versionInfo}
            smallWidget={smallWidget}
            onClick={onClick}
            onRemove={onRemove}
            doNotShow={doNotShow}
            hasSelection={hasSelection}
            getPrintPromise={getPrintPromise}
            setFetchingPdf={setFetchingPdf}
            setPrintProgress={setPrintProgress}
            showPrint={!smallWidget}
          />
          {!smallWidget && (
            <DateRangeBar>
              <DateRangeLabel>{defaults.from}</DateRangeLabel>
              <ReactDatePicker
                selectedDate={fromDate}
                onChange={(d: Date) => d && setFromDate(d)}
                maximumDate={moment(toDate).subtract(1, 'days').toDate()}
              />
              <DateRangeLabel>{defaults.to}</DateRangeLabel>
              <ReactDatePicker
                selectedDate={toDate}
                onChange={(d: Date) => d && setToDate(d)}
                minimumDate={moment(fromDate).add(1, 'days').toDate()}
                maximumDate={moment().toDate()}
              />
            </DateRangeBar>
          )}
          <AgGridWidgetWrapper
            id="cross-patient-idg-body"
            smallWidget={smallWidget}
            heightOffset={smallWidget ? undefined : DATE_BAR_HEIGHT_PX}
          >
            <AgGridTableWrapper
              className={`${gridTheme.className} light max-width`}
              style={{ height: '100%' }}
            >
              <AgGridReact
                gridOptions={gridOptions}
                columnDefs={masterColumnDefs}
                rowData={gridRowData}
                rowHeight={gridRowHeight}
                headerHeight={gridHeaderHeight}
                frameworkComponents={{
                  patientDetailRenderer: PatientDetailRenderer,
                }}
                suppressCellSelection
                suppressScrollOnNewData
                detailRowAutoHeight
                onGridReady={onGridReady}
                overlayNoRowsTemplate={overlayNoRowsTemplate}
              />
            </AgGridTableWrapper>
          </AgGridWidgetWrapper>
        </AppContent>
      </AppRoot>
    </IDGSelectionContext.Provider>
  );
};
